import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { HiOutlineEnvelope, HiOutlineLockClosed, HiOutlineEye, HiOutlineEyeSlash, HiOutlineChevronLeft } from "react-icons/hi2";
import logo from "../assets/logo.png";
import OtpInput from "../components/OtpInput";
import Api from "../service/http";

const ForgetPassword = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    otpSend: false,
    otp: "",
    verified: false,
    password: "",
    showPassword: false,
    confirmPassword: "",
    showConfirmPassword: false,
    buttonText: "Send OTP",
    buttonDisabled: false,
    countDown: 0,
  });
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name] : value,
    }));
  };

  const togglePasswordVisibility = () => {
    setFormData((prev) => ({
      ...prev,
      showPassword: !prev.showPassword,
    }));
  };

  const toggleConfirmPasswordVisibility = () => {
    setFormData((prev) => ({
      ...prev,
      showConfirmPassword: !prev.showConfirmPassword,
    }));
  };

  const resendOtp = async (e) => {
    e.preventDefault();
    if (!formData.email) {
        setError("Please enter your email address");
        return;
    }
    setLoading(true);
      const res = await Api.post("/web/resend-otp", { email: formData?.email })
      if(res?.status == 200) {
        setFormData((prev) => ({
          ...prev,
          otpSend: true,
          buttonText: "Verify OTP",
          verified: false,
          countDown: 60,
        }));
        setError("");
      } else {
        setError(res?.data?.message || "Failed to send OTP");
      }
    setLoading(false);
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if(formData?.buttonText == "Send OTP") {
      const res = await Api.post("/web/send-otp", { email: formData?.email })
      if(res?.status == 200) {
        setFormData((prev) => ({
          ...prev,
          otpSend: true,
          buttonText: "Verify OTP",
          countDown: 60,
        }));
        setError("");
      } else {
        setError(res?.data?.message || "Failed to send OTP");
      }
    }

    if(formData?.buttonText == "Verify OTP") {
      const res = await Api.post("/web/verify-otp", { email: formData?.email, otp: formData?.otp })
      if(res?.status == 200) {
        setFormData((prev) => ({
          ...prev,
          verified: true,
          buttonText: "Reset Password",
          buttonDisabled: false,
        }));
        setError("");
      } else {
        setError(res?.data?.message || "Invalid OTP code");
      }
    }

    if(formData?.buttonText == "Reset Password") {
      const res = await Api.post("/web/reset-password", { 
        email: formData?.email, 
        otp: formData?.otp,
        password: formData?.password,
        confirm_password: formData?.confirmPassword,
      })
      if(res?.status == 200) {
        alert(res?.data?.message || "Password reset successfully");
        navigate("/signin");
        setError("");
      } else {
        setError(res?.data?.message || "Failed to reset password");
      }
    }

    setLoading(false);
  };

  useEffect(() => {
    let interval;
    if (formData?.countDown > 0) {
      interval = setInterval(() => {
        setFormData((prev) => ({
          ...prev,
          countDown: prev.countDown - 1,
        }));
      }, 1000);
    } else if (formData?.countDown === 0) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }
  , [formData?.countDown]);

  return (
    <div className="h-screen flex items-center justify-center bg-[#f8f7fa] dark:bg-[#25293c] p-4 overflow-hidden">
      <div className="w-full max-w-md bg-white dark:bg-[#2f3349] rounded-2xl shadow-xl overflow-hidden transition-all duration-300 max-h-[95vh] flex flex-col">
        <div className="p-6 sm:p-8 overflow-y-auto scrollbar-hide">
          <Link to="/" className="flex flex-col items-center mb-8 text-center hover:opacity-80 transition-opacity">
            <div className="w-16 h-16 bg-indigo-50 dark:bg-indigo-500/10 rounded-2xl flex items-center justify-center mb-4 transition-colors">
              <img src={logo} alt="Logo" className="h-10 w-10 object-contain" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Forgot Password? 🔒</h1>
          </Link>
          <div className="flex flex-col items-center mb-8 text-center -mt-8">
            <p className="text-gray-500 dark:text-gray-400 mt-1 px-4">Enter your email and we'll send you instructions to reset your password</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl flex items-center gap-3 animate-shake">
              <div className="w-2 h-2 rounded-full bg-red-500"></div>
              <p className="text-sm text-red-600 dark:text-red-400 font-medium">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Field */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 ml-1">Email</label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-indigo-500 transition-colors">
                  <HiOutlineEnvelope size={20} />
                </div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="name@example.com"
                  disabled={formData.otpSend}
                  className="w-full pl-11 pr-4 py-2.5 bg-gray-50 dark:bg-[#25293c] border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:text-white transition-all placeholder:text-gray-400 disabled:opacity-60"
                  required
                />
              </div>
            </div>

            {formData?.otpSend && (
              <div className="space-y-4 pt-2">
                <div className="flex flex-col items-center gap-3">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Enter 6-Digit Verification Code</label>
                  <OtpInput 
                    length={6} 
                    value={formData.otp} 
                    onChange={(value)=> {
                      setFormData((prev) => ({ ...prev, otp : value }));
                    }} 
                  />
                </div>

                <div className="flex justify-center">
                  <button 
                    type="button"
                    disabled={formData?.countDown > 0 || loading} 
                    className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2" 
                    onClick={resendOtp}
                  >
                    {formData?.countDown > 0 ? (
                      <>
                        <div className="w-4 h-4 border-2 border-indigo-600/30 border-t-indigo-600 rounded-full animate-spin"></div>
                        <span>Resend in {formData?.countDown}s</span>
                      </>
                    ) : "Resend Code"}
                  </button>
                </div>

                {formData?.verified && (
                  <div className="space-y-4 animate-fadeIn">
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300 ml-1">New Password</label>
                      <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-indigo-500 transition-colors">
                          <HiOutlineLockClosed size={20} />
                        </div>
                        <input
                          type={formData?.showPassword ? "text" : "password"}
                          name="password"
                          value={formData?.password}
                          onChange={handleChange}
                          placeholder="••••••••"
                          className="w-full pl-11 pr-12 py-2.5 bg-gray-50 dark:bg-[#25293c] border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:text-white transition-all placeholder:text-gray-400"
                        />
                        <button
                          type="button"
                          onClick={togglePasswordVisibility}
                          className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                        >
                          {formData?.showPassword ? <HiOutlineEyeSlash size={20} /> : <HiOutlineEye size={20} />}
                        </button>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300 ml-1">Confirm Password</label>
                      <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-indigo-500 transition-colors">
                          <HiOutlineLockClosed size={20} />
                        </div>
                        <input
                          type={formData?.showConfirmPassword ? "text" : "password"}
                          name="confirmPassword"
                          value={formData?.confirmPassword}
                          onChange={handleChange}
                          placeholder="••••••••"
                          className="w-full pl-11 pr-12 py-2.5 bg-gray-50 dark:bg-[#25293c] border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:text-white transition-all placeholder:text-gray-400"
                        />
                        <button
                          type="button"
                          onClick={toggleConfirmPasswordVisibility}
                          className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                        >
                          {formData?.showConfirmPassword ? <HiOutlineEyeSlash size={20} /> : <HiOutlineEye size={20} />}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || formData?.buttonDisabled}
              className="w-full bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white py-3 rounded-xl font-bold shadow-lg shadow-indigo-200 dark:shadow-none transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              ) : (
                formData?.buttonText
              )}
            </button>

            <div className="flex justify-center mt-6">
              <Link to="/signin" className="flex items-center gap-1.5 text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors group">
                <HiOutlineChevronLeft size={16} className="group-hover:-translate-x-0.5 transition-transform" />
                Back to Sign In
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgetPassword;
